import {
  collectLeafRows, groupsThousands, numericTotalColumnIds, showDollarNumeric, summableColumns, toNumber,
} from '../reportGridUtils'
import { exportCell } from '../exportCell'

/**
 * Adapt a STORED job result (a CB report object) into the same `view` shape the
 * grids publish, so a finished queue run can be exported without mounting a grid
 * (spec 059 — the attachment/download path).
 *
 * Why an adapter instead of reusing the grid's `getView`: that one is built from
 * live TanStack state (visible columns, current sort, active filters). A queued
 * result has no grid and no user view — it is the SERVER's result, whole. So this
 * deliberately produces the unfiltered, server-ordered view: every column
 * visible, rows in the order CB returned them.
 *
 * The one thing it must get right, and the reason it isn't a two-line map:
 * **numeric columns have to carry real numbers, not HTML-stripped strings**,
 * or every figure lands in Excel as text and nothing sums (CLAUDE.md, CC-49370).
 */
export function viewFromJobResult(report) {
  const cbColumns = report?.columns || []
  if (!cbColumns.length) return null

  const rows = (collectLeafRows(report?.reportData) || []).map((r) => {
    const o = {}
    for (const cell of r.dataCells || []) {
      o[String(cell.columnId)] = { display: cell.displayValue, sortVal: cell.dataValue }
    }
    return o
  })

  // A column is numeric by the rule BOTH grids' export views use, so a downloaded
  // result and an on-screen export agree: CB right-aligns it or types it as a
  // number, or its grand total is a number (`showDollarNumeric`, CC-49872's
  // fallback). Spec 196 (CC-54309): this used the first half only, so WIP - AR -
  // Trust (155) — ten money columns CB declares DECIMAL but LEFT-aligns with
  // filter type NONE — downloaded from the drawer as text ("7,854,089.45") that
  // Excel cannot add, while the page's own Excel stored every one as a number.
  const totaledIds = numericTotalColumnIds(report)
  const numericIds = new Set(cbColumns.filter((c) => showDollarNumeric(c, totaledIds)).map((c) => String(c.columnId)))

  // SUM-only guardrail (CC-49382): only columns whose server total genuinely
  // equals the sum of every row may be totalled. Averages and rates keep the
  // server's figure rather than being re-added into a wrong number.
  let summable = new Set()
  try {
    summable = summableColumns(report, rows) || new Set()
  } catch {
    summable = new Set()
  }

  const columns = cbColumns.map((col, idx) => {
    const numeric = numericIds.has(String(col.columnId))
    // Spec 211: grouped on screen, text here — the PDF groups it too.
    const thousands = !numeric && groupsThousands(col, totaledIds)
    return {
      key: String(col.columnId),
      label: col.label,
      visible: true,
      order: idx,
      numeric,
      summable: summable.has(String(col.columnId)),
      serverTotal: '',
      ...(thousands ? { thousands } : {}),
    }
  })

  // Spec 133: one cell converter for every view builder (exportCell).
  const toValues = (row) => Object.fromEntries(cbColumns.map((col) => {
    const id = String(col.columnId)
    return [id, exportCell(row[id]?.display ?? '', row[id]?.sortVal, numericIds.has(id))]
  }))

  const values = rows.map(toValues)

  // CB's own footer, verbatim. Never recomputed — these are the server's figures
  // and a financial report's totals are not ours to re-derive.
  const footerRows = report?.reportFooterRows?.length
    ? report.reportFooterRows
    : (report?.reportFooterCells?.length ? [report.reportFooterCells] : [])
  const footer = footerRows.map((cells) => Object.fromEntries(cbColumns.map((col) => {
    const id = String(col.columnId)
    const cell = (cells || []).find((cc) => String(cc.columnId) === id)
    // A footer cell's number is read from its display, as before.
    return [id, exportCell(cell?.displayValue ?? '', undefined, numericIds.has(id))]
  })))

  return {
    columns,
    // No grid means no filtering and no user sort, so both row sets are the same
    // server-ordered result — the export scope flags then become no-ops, which is
    // exactly right for "download what the server produced".
    visibleRows: values,
    allRows: values,
    footerRows: footer,
  }
}

/**
 * A value that is "money-ish" — carries a currency symbol, thousands separator,
 * decimal point or percent. Used to decide whether a column of parseable digits is
 * a QUANTITY or an IDENTIFIER, which matters more than it looks: "Account Number"
 * `10101` and "Ref Number" `00123` both parse as numbers, and writing them to Excel
 * as numbers gives an account a thousands separator and silently eats a ref
 * number's leading zeros.
 */
const MONEYISH = /[$.,%]/

/**
 * Adapt a stored SPECIALIZED or ACCOUNTING job result into the same `view` shape
 * the grids publish (CC-50233).
 *
 * ## Why this is a second adapter rather than a branch in the one above
 *
 * `viewFromJobResult` reads CB's own generic-report object (`Columns` +
 * `ReportData` dataCells). Specialized and accounting jobs never produce that:
 * they replay their OWN endpoint and store what it returned, which for the
 * CSV-backed reports is `{ data: { headers: [...], rows: [{ header: value }] } }`.
 * Two genuinely different wire shapes, so two adapters and one caller that picks.
 *
 * ## Why those jobs could not be downloaded at all before
 *
 * The drawer gated its Excel/PDF items on `job.processId`, and a processId only
 * exists for the generic path — CB mints one from `GenerateReportData`. Every
 * specialized and accounting job therefore carries `process_id = NULL` by design,
 * so the affordance was silently absent on all of them while "Open report" (which
 * needs only `reportId`) stayed visible. Verified live on QA 2026-08-17: 12 of the
 * 40 most recent ready jobs were in that state. That is what QA reported as
 * "the download buttons are not present at all" (CC-50233).
 *
 * ## What it deliberately does NOT do
 *
 * - **No totals.** These payloads carry no server-declared aggregate, so nothing
 *   here is summable and `footerRows` is empty. Some of them (Time Entries, the
 *   General Ledger) already include the server's own "Grand Total" row inside
 *   `rows`, and it is passed through verbatim as the row it is. Re-deriving a
 *   financial total from formatted strings is exactly the thing the repo forbids.
 * - **No tree flattening.** P&L, Trial Balance and AR store shapes that are not a
 *   table at all (`{ success, report: { sections } }`, and AR's grouped
 *   `{ rows, columnSettings }`). This returns `null` for them, and the caller says
 *   so out loud rather than inventing a table — a wrong statement is worse than an
 *   absent one.
 *
 * @returns a view, or `null` when the payload is not a flat table.
 */
export function viewFromTabularResult(payload) {
  const d = payload?.data ?? payload
  const headers = (Array.isArray(d?.headers) ? d.headers : [])
    .map((h) => (h == null ? '' : String(h)))
    .filter((h) => h !== '')
  const rows = Array.isArray(d?.rows) ? d.rows : []
  if (headers.length === 0) return null
  // Keyed row objects only. AR stores rows too, but they are its own grouped
  // record shape with no `headers` to pair them with — hence the headers check
  // above, and this guard for anything else array-shaped.
  if (rows.length > 0 && (typeof rows[0] !== 'object' || Array.isArray(rows[0]))) return null

  // Spec 193: these values are the TEXT CB's CSV export holds — a client name, a
  // memo — not grid HTML, so they are read as typed. Stripping "tags" from them
  // deleted real text: `Client Three & Sons <Ltd>` downloaded as "Client Three &
  // Sons", and a literal "&amp;" as "&".
  const cell = (row, h) => {
    const raw = row?.[h]
    if (typeof raw === 'number') return { text: String(raw), num: raw }
    const text = String(raw ?? '').trim()
    return { text, num: toNumber(text) }
  }

  /**
   * A column is numeric when every non-blank value parses AND at least one of them
   * is formatted like a quantity. A column of bare integers stays TEXT on purpose
   * (see MONEYISH) — the safe direction, because it can never turn an identifier
   * into a number, only leave a count as a string.
   */
  const numeric = new Set(
    headers.filter((h) => {
      let sawValue = false
      let sawFormatted = false
      for (const row of rows) {
        const { text, num } = cell(row, h)
        if (text === '') continue
        if (num == null) return false
        sawValue = true
        if (typeof row?.[h] === 'number' || MONEYISH.test(text)) sawFormatted = true
      }
      return sawValue && sawFormatted
    })
  )

  const columns = headers.map((h, idx) => ({
    key: h,
    label: h,
    visible: true,
    order: idx,
    numeric: numeric.has(h),
    // Nothing is summable: no aggregate was declared, and the server's own total
    // row (where there is one) is already in `rows`.
    summable: false,
    serverTotal: '',
  }))

  const values = rows.map((row) =>
    Object.fromEntries(
      headers.map((h) => {
        const { text, num } = cell(row, h)
        // Real numbers for numeric columns or every figure lands in Excel as text
        // and nothing sums (CLAUDE.md, CC-49370). A value we cannot parse with
        // confidence stays the string it was rather than becoming a wrong number.
        return [h, numeric.has(h) && num != null ? num : text]
      })
    )
  )

  return { columns, visibleRows: values, allRows: values, footerRows: [] }
}

export default viewFromJobResult
